import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { cxConsumerOnboardingApi, GetQuantityWiseResourceParams } from "./api";
import { CXCxOnboardingApi } from "../consumer-onboarding/api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { queryClient } from "@shared/api/queries/queryClient";
import { toast } from "@shared/ui/use-toast";

export const useCategory = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.category(),
    () => cxConsumerOnboardingApi.getCategory(),
  );
};

export const useSubCategory = (code_list: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.subCategory(code_list),
    () => cxConsumerOnboardingApi.getSubCategory(code_list),
  );
};

export const useCity = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.city(),
    () => cxConsumerOnboardingApi.getCity(),
  );
};
export const useOwnerType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.ownerType(),
    () => cxConsumerOnboardingApi.getOwnerType(),
  );
};
export const usePremises = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.premises(),
    () => cxConsumerOnboardingApi.getPremises(),
  );
};
export const usePlans = (consumerUtilityServices?: string[]) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.plans(),
    () => cxConsumerOnboardingApi.getPlans(consumerUtilityServices),
  );
};
export const useVerifyEmail = (email: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.verifyEmail(email),
    () => cxConsumerOnboardingApi.verifyEmail(email),
  );
};

export const useUtilityServices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.utilityServices(),
    () => cxConsumerOnboardingApi.getUtilityServices(),
  );
};

export const useConsumerRelation = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.consumerRelation(), // Unique query key
    () => cxConsumerOnboardingApi.getConsumerRelation(),
  );
};

export const useAddConsumer = () => {
  return useSmartMutation((payload: any) =>
    cxConsumerOnboardingApi.addConsumer(payload),
  );
};

export const useUpdateConsumer = () => {
  return useSmartMutation(
    ({ consumerId, payload }: { consumerId: string; payload: any }) =>
      cxConsumerOnboardingApi.updateConsumer(consumerId, payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.refetchQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.refetchQueries({
          queryKey: ["cxonboarding", "serviceOrderConsumerforInstallation"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "connectionDetail"],
        });
        toast({
          title: "Success",
          description: "Consumer updated successfully.",
          variant: "default",
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update consumer.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateProgressStatus = () => {
  return useSmartMutation(
    ({ consumerId, payload }: { consumerId: string; payload: any }) =>
      cxConsumerOnboardingApi.updateProgressStatus(consumerId, payload),
  );
};

export const useAcceptConsumer = () => {
  return useSmartMutation((consumerId: string) =>
    cxConsumerOnboardingApi.acceptConsumer(consumerId),
  );
};

export const useRejectConsumer = () => {
  return useSmartMutation((consumerId: string) =>
    cxConsumerOnboardingApi.rejectConsumer(consumerId),
  );
};

export const useSurveyInProgess = () => {
  return useSmartMutation(
    ({ consumerId, payload }: { consumerId: string; payload: any }) =>
      cxConsumerOnboardingApi.surveyInProgress(consumerId, payload),
  );
};

export const useSkipInspection = () => {
  return useSmartMutation(({ consumerId }: { consumerId: string }) =>
    cxConsumerOnboardingApi.skipInspection(consumerId),
  );
};

export const useApproveConsumer = () => {
  return useSmartMutation(
    ({ consumerId, payload }: { consumerId: string; payload: any }) =>
      cxConsumerOnboardingApi.approveConsumer(consumerId, payload),
  );
};

export const useConsumerDetail = (consumer_id: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.consumerDetail(consumer_id),
    () => cxConsumerOnboardingApi.getConsumerDetail(consumer_id),
    {
      enabled: !!consumer_id,
    },
  );
};

export const useServiceOrder = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.serviceOrder(),
    () => cxConsumerOnboardingApi.getServiceOrder(),
  );
};
export const useVerifyRejectDocuments = () => {
  return useSmartMutation(
    ({ payload, documentId }: { payload: any; documentId: any }) =>
      cxConsumerOnboardingApi.verifyRejectDocuments(payload, documentId),
  );
};

export const useCategoryFees = (category: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.categoryFees(category),
    () => cxConsumerOnboardingApi.getCategoryFees(category),
    {
      enabled: !!category,
    },
  );
};

export const usePremiseInfo = (premise: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.premiseInfo(premise),
    () => cxConsumerOnboardingApi.getPremiseInfo(premise),
  );
};

export const useCheckUnitWiseConsumer = () => {
  return useSmartMutation(
    ({
      unitCode,
      remoteUtilityId,
      isCurrent = 1,
    }: {
      unitCode: string;
      remoteUtilityId: string | number;
      isCurrent?: number;
    }) =>
      cxConsumerOnboardingApi.getUnitWiseConsumer(
        unitCode,
        remoteUtilityId,
        isCurrent,
      ),
  );
};

export const usePlanDetails = (
  remotePlanId: string | number,
  remoteUtilityId: string | number,
) => {
  return useSmartQuery(
    ["cx", "consumerOnboarding", "planDetails", remotePlanId, remoteUtilityId],
    () => cxConsumerOnboardingApi.getPlanDetails(remotePlanId, remoteUtilityId),
    {
      enabled: !!remotePlanId && !!remoteUtilityId,
    },
  );
};

export const useCreatePlanPayment = () => {
  return useSmartMutation((payload: any) =>
    cxConsumerOnboardingApi.addPlanPayment(payload),
  );
};
export const useGetPlanParameters = (
  planId: string | number,
  remoteUtilityId: string | number,
  utilityService: string[],
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.planParameters(
      planId,
      remoteUtilityId,
      utilityService,
    ),
    () =>
      cxConsumerOnboardingApi.getPlanParameters(
        planId,
        remoteUtilityId,
        utilityService,
      ),
    {
      enabled:
        !!planId &&
        !!remoteUtilityId &&
        Array.isArray(utilityService) &&
        utilityService.length > 0,
    },
  );
};

export const useRegistrationCharges = (
  planId: string | number,
  remoteUtilityId: string | number,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.registrationCharges(
      planId,
      remoteUtilityId,
    ),
    () =>
      cxConsumerOnboardingApi.getRegistrationCharges(planId, remoteUtilityId),
    {
      enabled: !!planId && !!remoteUtilityId,
    },
  );
};

export const useTerritoryJson = (configLevel: string, code_list: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const queryKey = QueryKeyFactory.module.cx.consumerOnboarding.territoryJson(
    remoteUtilityId,
    configLevel,
    code_list,
  );
  const enabled = !!remoteUtilityId && !!configLevel && !!code_list;

  return useSmartQuery(
    queryKey as unknown[],
    () =>
      cxConsumerOnboardingApi.getTerritoryJson(
        remoteUtilityId,
        configLevel,
        code_list,
      ),
    { enabled },
  );
};

export const useTerritoryPremiseStats = (territoryCode: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const queryKey =
    QueryKeyFactory.module.cx.consumerOnboarding.territoryPremiseStats(
      remoteUtilityId,
      territoryCode,
    );
  const enabled =
    !!remoteUtilityId &&
    !!territoryCode &&
    (territoryCode.includes("#") || territoryCode.includes("_"));

  return useSmartQuery(
    queryKey as unknown[],
    () =>
      cxConsumerOnboardingApi.getTerritoryPremiseStats(
        remoteUtilityId,
        territoryCode,
      ),
    { enabled },
  );
};

export const useUpdateAssignedServiceAddress = () => {
  return useSmartMutation(
    (data: { consumerId: string | number; payload: any }) =>
      cxConsumerOnboardingApi.updateAssignedServiceAddress(
        data.consumerId,
        data.payload,
      ),
  );
};

export const useUpdateConsumerUnitPlan = () => {
  return useSmartMutation(
    (data: {
      consumerId: string | number;
      payload: { remote_plan_id: number; unit_code: string };
    }) =>
      cxConsumerOnboardingApi.updateConsumerUnitPlan(
        data.consumerId,
        data.payload,
      ),
  );
};

export const useGetQuantityWiseResource = (
  params: GetQuantityWiseResourceParams = {},
  options?: { enabled?: boolean },
) => {
  const remoteUtilityIdFromStore = useRemoteUtilityId();
  const remoteUtilityId = params.remoteUtilityId ?? remoteUtilityIdFromStore;

  const queryParams = {
    ...params,
    remoteUtilityId,
  };

  const key = [
    "cx",
    "quantity-wise",
    "resource",
    remoteUtilityId,
    params.consumerId,
  ];

  return useSmartQuery(
    key,
    () => cxConsumerOnboardingApi.getQuantityWiseResource(queryParams),
    {
      enabled:
        options?.enabled !== undefined
          ? options.enabled
          : !!remoteUtilityId && !!params.consumerId,
    },
  );
};

export const useQuantityWiseResource = useGetQuantityWiseResource;

export const useUpdateConsumerStatus = () => {
  return useSmartMutation(
    (data: { consumerId: string | number; payload: { status: number } }) =>
      cxConsumerOnboardingApi.updateConsumerStatus(
        data.consumerId,
        data.payload,
      ),
  );
};
